// Audience extracts — the ops dashboard's approved marketing audiences,
// relayed by /api/v1/audience-extracts. Through the shared apiV1 client so
// the session cookie / token goes with every call.
import { apiV1 } from '@/api/client';

const BASE = '/audience-extracts';

// 'all' (or nothing) is no filter. entity is Branch / Franchise.
const filterParams = ({ region, office, entity } = {}) => {
  const params = {};
  if (region && region !== 'all') params.region = region;
  if (office && office !== 'all') params.office = office;
  if (entity && entity !== 'all') params.entity = entity;
  return params;
};

// Content-Disposition: attachment; filename="seed_s2_meta_20261101.csv"
const filenameFrom = (disposition, fallback) => {
  const m = /filename="?([^";]+)"?/i.exec(disposition || '');
  return (m && m[1]) || fallback;
};

// Save a blob response as a file. The file only ever lives in the browser
// on its way to the user's disk; LinkHub keeps no copy.
const saveBlob = (response, fallback) => {
  const name = filenameFrom(response.headers?.['content-disposition'], fallback);
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return {
    filename: name,
    rows: response.headers?.['x-row-count'],
    buildId: response.headers?.['x-build-id'],
  };
};

// A failed blob request carries its JSON error as a Blob: read it back so
// the user sees the server's sentence, not "An error occurred".
export async function blobErrorMessage(error) {
  const data = error?.response?.data;
  if (data && typeof data.text === 'function') {
    try {
      const body = JSON.parse(await data.text());
      const d = body?.detail;
      return (typeof d === 'string' ? d : d?.message) || error.message;
    } catch {
      /* not JSON */
    }
  }
  return error?.message || 'Download failed';
}

const audienceExtractsApi = {
  async list(filters) {
    const res = await apiV1.get(`${BASE}/`, { params: filterParams(filters) });
    return res.data?.data;
  },
  async areas() {
    const res = await apiV1.get(`${BASE}/areas`);
    return res.data?.data;
  },
  async download(key, platform, filters) {
    const res = await apiV1.get(`${BASE}/${encodeURIComponent(key)}/download`, {
      params: { platform, ...filterParams(filters) },
      responseType: 'blob',
      timeout: 180000,
    });
    return saveBlob(res, `${key}_${platform}.csv`);
  },
  async downloadAreasCsv() {
    const res = await apiV1.get(`${BASE}/areas`, {
      params: { format: 'csv' },
      responseType: 'blob',
    });
    return saveBlob(res, 'high_value_areas.csv');
  },
  async requestRebuild() {
    const res = await apiV1.post(`${BASE}/rebuild`);
    return res.data?.data;
  },
};

export default audienceExtractsApi;
